import styles from "../css/MarkedTermList.module.css";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../hook";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import Term from "../Term";
import TermListGrid from "../custom_view/TermListGrid";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import { QuestionType } from "../../settings/model/QuestionType";
import { getSelectedGroupsFromLocalStorage } from "../../settings/util/termListSettings";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import Group from "../../groups/Group";
import { makeSelectedGroupsString } from "../../groups/groups";
import { getFilteredTermsWithOptions, keepLocalTermState } from "../terms";
import { selectTerms } from "../termSlice";
import BasicMenu, { BasicMenuButton } from "../../common/BasicMenu";
import BigButton from "../../common/BigButton";
import VoiceLanguageSelectScreen from "../../voice/view/VoiceLanguageSelectScreen";
import Language from "../../language/Language";
import { setUserCustomLanguage } from "../../sentence/manager/sentenceSettings";
import ListEmptyText from "../../common/ListEmptyText";
import { fetchSentence } from "../../sentence/service/sentenceAPI";
import { generateMoreSentence } from "../../sentence/service/moreSentence";
import SentenceHistory from "../../sentence/model/SentenceHistory";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import DropDownButton from "../../common/view/DropDownButton";
import { TermSentenceWithStringValue } from "../../sentence/model/Sentence";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface MarkedTermListProps {
  open: boolean;
  handleOnClickMarkButton: (term: Term) => void;
  handleOnClickLevelButton: (term: Term) => void;
  handleOnClickTermEditButton: (term: Term) => void;
  handleOnClickTermDeleteButton: (term: Term) => void;
  handleOnClickCopySentenceToMemoButton: (
    item: TermSentenceWithStringValue
  ) => void;
  onClickLearnButton: () => void;
  onClose: () => void;
}

export default function MarkedTermListScreen(props: MarkedTermListProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const rawTerms = useAppSelector(selectTerms);

  const [selectedTerm, setSelectedTerm] = useState<Term | null>(null);
  const [sentenceHistory] = useState(() => new SentenceHistory());
  const [selectedGroups, setSelectedGroups] = useState<Group[]>();
  const [termList, setTermList] = useState<Term[]>([]);

  const [title, setTitle] = useState<string>("");

  const [questionType, setQuestionType] = useState(QuestionType.termFirst);
  const [pronPosition, setPronPosition] = useState(
    PronunciationPosition.onTerm
  );

  const [isOpenTermCellMoreMenu, setIsOpenTermCellMoreMenu] =
    useState<Term | null>(null);
  const [
    isOpenSentenceLanguageSelectScreen,
    setIsOpenSentenceLanguageSelectScreen,
  ] = useState(false);
  const [showingGroupSelect, setShowingGroupSelect] = useState(false);
  const [showingSentenceMoreMenu, setShowingSentenceMoreMenu] =
    useState<TermSentenceWithStringValue>();

  const getTermCellMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("Edit"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          props.handleOnClickTermEditButton(term);
        }
        handleOnCloseTermCellMoreMenu();
      },
    },
    {
      title: t("Delete"),
      handleOnClose: () => {
        const term = isOpenTermCellMoreMenu;
        if (term !== null) {
          props.handleOnClickTermDeleteButton(term);
        }
        handleOnCloseTermCellMoreMenu();
      },
    },
  ];

  const getTermCellSentenceMoreButtons = (): BasicMenuButton[] => [
    {
      title: t("ExampleSaveToTermMemo"),
      handleOnClose: () => {
        const item = showingSentenceMoreMenu;
        if (item !== undefined) {
          props.handleOnClickCopySentenceToMemoButton(item);
        }
        handleOnCloseTermCellSentenceMoreMenu();
      },
    },
  ];

  useEffect(() => {
    setQuestionType(QuestionType.showAll);
    setPronPosition(PronunciationPosition.onTerm);
    setSelectedGroups(getSelectedGroupsFromLocalStorage());
  }, [props.open]);

  useEffect(() => {
    updateMarkedTermList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTerms]);

  useEffect(() => {
    if (selectedGroups === undefined) return;
    const newList = getFilteredTermsWithOptions(
      rawTerms,
      undefined,
      undefined,
      selectedGroups,
      undefined,
      true
    );

    const groupName = makeSelectedGroupsString(selectedGroups);
    setTitle(groupName);
    setTermList(newList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups]);

  function updateMarkedTermList() {
    const rawById = new Map(rawTerms.map((t) => [t.id, t]));
    setTermList((prevList) => {
      const newList = prevList
        .filter((t) => rawById.has(t.id))
        .map((t) => rawById.get(t.id)!);
      return keepLocalTermState(newList, prevList);
    });
  }

  function handleOnCloseTermCellMoreMenu() {
    setIsOpenTermCellMoreMenu(null);
  }

  function handleOnCloseTermCellSentenceMoreMenu() {
    setShowingSentenceMoreMenu(undefined);
  }

  function handleOnClickContainer(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  function handleOnClickTerm(term: Term, isSelectionMode: boolean) {
    const termIndex = termList.findIndex((t) => t.id === term.id);
    const newTerms = [...termList];
    if (termIndex !== -1) {
      if (isSelectionMode) {
        newTerms[termIndex] = {
          ...newTerms[termIndex],
          selected: !newTerms[termIndex].selected,
        };
      } else {
        newTerms[termIndex] = {
          ...newTerms[termIndex],
          isShowDetail: !newTerms[termIndex].isShowDetail,
        };
      }
    }
    setTermList(newTerms);
  }

  function handleOnClickSentenceMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    item: TermSentenceWithStringValue
  ) {
    setAnchorEl(event.currentTarget);
    setShowingSentenceMoreMenu(item);
  }

  function handleOnClickTermCellMoreButton(
    event: React.MouseEvent<HTMLDivElement>,
    term: Term
  ) {
    setAnchorEl(event.currentTarget);
    setIsOpenTermCellMoreMenu(term);
  }

  function handleOnClickGenerateSentenceButton(term: Term) {
    if (term.sentence === undefined) {
      generateSentence(term);
    } else {
      deleteSentence(term);
    }
  }

  function handleOnClickSentenceLanguageButton(term: Term) {
    setSelectedTerm(term);
    setIsOpenSentenceLanguageSelectScreen(true);
  }

  function handleOnClickMoreSentenceButton(term: Term) {
    generateMoreSentence(term, setTermList, sentenceHistory);
  }

  function updateSentenceLanguage(language: Language) {
    setUserCustomLanguage(language.code);

    if (selectedTerm != null) {
      generateSentence(selectedTerm);
    }
  }

  async function generateSentence(term: Term) {
    setTermList((prevTerms) => {
      const index = prevTerms.findIndex((t) => t.id === term.id);
      if (index === -1) return prevTerms;

      const updated = [...prevTerms];
      updated[index] = {
        ...updated[index],
        isGeneratingSentence: true,
      };
      return updated;
    });

    try {
      const languageCode =
        term.group.length !== 0 ? await fetchGroupLanguageCode(term.group) : "";
      const sentence = await fetchSentence(
        term.term,
        languageCode,
        sentenceHistory
      );

      setTermList((prevTerms) => {
        const index = prevTerms.findIndex((t) => t.id === term.id);
        if (index === -1) return prevTerms;

        const updated = [...prevTerms];
        updated[index] = {
          ...updated[index],
          sentence,
          isGeneratingSentence: false,
        };
        return updated;
      });
    } catch (error) {
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function deleteSentence(term: Term) {
    const termIndex = termList.findIndex((t) => t.id === term.id);

    if (termIndex !== -1) {
      const newTerms = [...termList];
      const { sentence, ...rest } = newTerms[termIndex];
      const newTerm = { ...rest };

      newTerms[termIndex] = newTerm;

      setTermList(newTerms);
    }
  }

  if (props.open === false) return null;

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.main} onClick={props.onClose}>
          <div className={styles.container} onClick={handleOnClickContainer}>
            <div className={styles.contentContainer}>
              <ContainerNavigationView
                open={true}
                titleView={
                  <DropDownButton
                    title={title}
                    handleOnClick={() => {
                      setShowingGroupSelect(true);
                    }}
                  />
                }
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={props.onClose}
              />
              <TermListGrid
                terms={termList}
                handleOnClick={handleOnClickTerm}
                handleOnClickMarkButton={props.handleOnClickMarkButton}
                handleOnClickLevelButton={props.handleOnClickLevelButton}
                handleOnClickMoreButton={(event, term) => {
                  handleOnClickTermCellMoreButton(event, term);
                }}
                handleOnClickGenerateSentenceButton={
                  handleOnClickGenerateSentenceButton
                }
                handleOnClickSentenceLanguageButton={
                  handleOnClickSentenceLanguageButton
                }
                handleOnClickMoreSentenceButton={
                  handleOnClickMoreSentenceButton
                }
                handleOnClickSentenceMoreButton={
                  handleOnClickSentenceMoreButton
                }
                config={{
                  style: "sheetList",
                  questionType: questionType,
                  pronunciationPosition: pronPosition,
                  isSelectionMode: false,
                }}
              />

              {termList.length === 0 && (
                <div className={styles.termsEmptyTextContainer}>
                  <ListEmptyText text={t("MarkedTermListEmptyLabel")} />
                </div>
              )}
            </div>

            {termList.length !== 0 && (
              <div className={styles.bottomContainer}>
                <BigButton
                  title={t("LearningButton")}
                  variant="normal"
                  handleClick={() => {
                    props.onClickLearnButton();
                  }}
                />
              </div>
            )}
          </div>
        </div>
        <BasicMenu
          isOpen={isOpenTermCellMoreMenu !== null}
          anchorEl={anchorEl}
          buttons={getTermCellMoreButtons()}
          handleOnClose={() => handleOnCloseTermCellMoreMenu()}
        />
        <BasicMenu
          isOpen={showingSentenceMoreMenu !== undefined}
          anchorEl={anchorEl}
          buttons={getTermCellSentenceMoreButtons()}
          handleOnClose={() => handleOnCloseTermCellSentenceMoreMenu()}
        />
        <VoiceLanguageSelectScreen
          open={isOpenSentenceLanguageSelectScreen === true}
          handleOnClickLanguage={updateSentenceLanguage}
          handleOnClose={() => {
            setIsOpenSentenceLanguageSelectScreen(false);
          }}
        />
        <GroupSelectScreen
          open={showingGroupSelect}
          showAllGroup={true}
          showNoGroup={true}
          selectStyle="multiple"
          showGroupAddButton={false}
          handleOnSelectGroups={(groups: Group[]) => {
            setSelectedGroups(groups);
          }}
          handleOnClose={() => {
            setShowingGroupSelect(false);
          }}
        />
      </div>
    </>
  );
}
